import { useEffect, useRef, useState } from "react"
import { GetMediaControls } from "./ColorFinderFootageDisplay"
import { ColorFilter, PanelOpenCloseButton } from "./ColorFinderPanel"
import { ColorFinderTiler } from "./ColorFinderTiler.class"
import { CumulativeRGB } from "@nova/component-color-finder-legend/src/web-workers/createCumulativeRgb/createCumulativeRgb"
import { ImageColorFinderFlight, VideoColorFinderFlight } from "@nova/protobuf"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    selectColorFinderAnomalyEnabled,
    selectColorFinderAnomalyOpacity,
    selectColorFinderAnomalySensitivity,
    selectColorFinderTool,
} from "./redux/selectors"
import {
    selectIconNumberStartAt,
    selectIconTemplate,
    updateIconTemplate,
} from "plugins/Toolbox/tool-registry"
import * as Icons from "@nova/component-icons"
import { IconButton } from "@nova/component-btn"
import { FormTextInput } from "@nova/component-form-text-input"
import {
    IconColorDropdown,
    IconDropdown,
    IconPackDropdown,
    IconShapeDropdown,
} from "components/IconDropdowns"
import { IconPackOption, IconShape } from "@nova/constants"
import { AlertContainer } from "@nova/component-alert-container"
import { ColorFinderFilter } from "./ColorFinderFilter"
import { Slider } from "components/Slider"
import {
    setColorFinderAnomalyEnabled,
    setColorFinderAnomalyOpacity,
    setColorFinderAnomalySensitivity,
    setColorFinderTool,
} from "./redux/slice"
import { ColorSample, ColorSampleFilter } from "./ColorSample"
import classNames from "classnames"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { Resizable } from "re-resizable"
import { Divider } from "components/Divider"
import { selectNextIconNumber } from "features/entity-caching/icons/selectors"
import { iconsByIconPack } from "features/map/inspector-panel/icons/constants"
import { numberIconColorSwatches } from "features/map/inspector-panel/icons/InspectorPanelIconLayer"
import { InspectorPanelIconNumberMenu } from "features/map/inspector-panel/icons/InspectorPanelIconNumberMenu"
import { defaultIconColor } from "features/map/layers/icons/constants"
import { encodeColorToNumber } from "@nova/serializer/src/client/util"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"
import { ToggleSwitch } from "@nova/component-toggle-switch"

const FILTER_ALERT_KEY = "cf-filter-alert"
export const ColorFinderLeftPanel = ({
    getMediaControls,
    onFilterChange,
    onAddSample,
    onRemoveSample,
    mapRef,
    tiler,
    cumulativeRgb,
    activeSamples,
}: {
    getMediaControls: GetMediaControls
    tiler: ColorFinderTiler
    onFilterChange: (newFilter: ColorFilter) => void
    onAddSample: (sample: ColorSampleFilter) => void
    onRemoveSample: (id: string) => void
    activeSamples: ColorSampleFilter[]
    mapRef: (node: HTMLDivElement | null) => void
    cumulativeRgb: CumulativeRGB
    flight: ImageColorFinderFlight | VideoColorFinderFlight
}) => {
    const dispatch = useAppDispatch()
    const [isOpen, setIsOpen] = useState(true)
    const [showAlert, setShowAlert] = useState(localStorage.getItem(FILTER_ALERT_KEY) !== "true")

    const activeTool = useAppSelector(selectColorFinderTool)
    const anomalyEnabled = useAppSelector(selectColorFinderAnomalyEnabled)
    const anomalySensitivity = useAppSelector(selectColorFinderAnomalySensitivity)
    const anomalyOpacity = useAppSelector(selectColorFinderAnomalyOpacity)

    const isPointTool = activeTool === "point"
    const isColorSampleTool = activeTool === "color-sample"

    const handleColorSampleComplete = (filter: ColorSampleFilter) => {
        dispatch(setColorFinderTool(""))
        onAddSample(filter)
    }

    const handleColorFilterChange = (filter: ColorFilter) => {
        dispatch(setColorFinderTool(""))
        onFilterChange(filter)
    }

    const iconTemplate = useAppSelector(selectIconTemplate)

    return (
        <>
            <div className="shrink-0 -mx-1">
                <div
                    className={classNames(
                        "absolute py-2 pl-5 pr-4 border border-transparent z-10",
                        {
                            hidden: isOpen,
                        }
                    )}
                >
                    <PanelOpenCloseButton isOpen={isOpen} onClick={() => setIsOpen(true)}>
                        <Icons.SmallIcon Icon={Icons.KeyboardDoubleArrowRight} />
                    </PanelOpenCloseButton>
                </div>
            </div>

            <div
                className={classNames(
                    "flex flex-col border border-white-200 rounded-lg min-h-0 w-[300px] h-full overflow-hidden",
                    {
                        hidden: !isOpen,
                    }
                )}
            >
                <div className="px-4 py-2 flex items-center gap-2 border-b border-b-gray-700">
                    {isColorSampleTool ? (
                        <div className="flex flex-col gap-2">
                            <IconButton
                                onClick={() => dispatch(setColorFinderTool(""))}
                                className="flex items-center gap-1"
                            >
                                <Icons.SmallIcon Icon={Icons.ArrowLeft} />
                                <small>Cancel</small>
                            </IconButton>
                            <div className="flex gap-2 text-white">
                                <Icons.SmallIcon Icon={Icons.Colorize} />
                                <h5>Sample from Image</h5>
                            </div>
                        </div>
                    ) : isPointTool ? (
                        <div className="flex flex-col gap-2">
                            <IconButton
                                onClick={() => dispatch(setColorFinderTool(""))}
                                className="flex items-center gap-1"
                            >
                                <Icons.SmallIcon Icon={Icons.ArrowLeft} />
                                <small>Cancel</small>
                            </IconButton>
                            <div className="flex gap-2 text-white">
                                <Icons.SmallIcon Icon={Icons.PinDrop} />
                                <h5>Create {iconTemplate.name}</h5>
                            </div>
                        </div>
                    ) : (
                        <>
                            <IconButton className="text-gray-500" onClick={() => setIsOpen(false)}>
                                <Icons.SmallIcon Icon={Icons.KeyboardDoubleArrowLeft} />
                            </IconButton>
                            <small>Filter by Color</small>
                        </>
                    )}
                </div>

                <div className="flex flex-col flex-1 min-h-0 relative">
                    <Resizable
                        enable={{ bottom: activeTool !== "color-sample" }}
                        minHeight="86px"
                        bounds="parent"
                        maxHeight={"calc(100% - 68px)"}
                        className="basis-auto"
                        handleComponent={{
                            bottom: (
                                <div className="z-10 group w-full">
                                    <div className="pb-1 bg-black-1000">
                                        <Divider className="group-hover:bg-blue-700 group-active:bg-blue-700" />
                                    </div>
                                </div>
                            ),
                        }}
                    >
                        <ScrollableArea className="h-full min-h-0" shouldPreventScrollbarShifting>
                            <div className="shrink-0">
                                <div className={classNames(!isPointTool && "hidden")}>
                                    <PointForm />
                                </div>
                                <div className={classNames("p-2", !isColorSampleTool && "hidden")}>
                                    <ColorSample onComplete={handleColorSampleComplete} />
                                </div>
                                <div
                                    className={classNames(
                                        "flex flex-col items-center gap-2.5 divide-y divide-white-200",
                                        (isPointTool || isColorSampleTool) && "hidden"
                                    )}
                                >
                                    {showAlert && (
                                        <AlertContainer
                                            type="info"
                                            body="Draw a shape on the graph below to search for colors."
                                            onClose={() => {
                                                localStorage.setItem(FILTER_ALERT_KEY, "true")
                                                setShowAlert(false)
                                            }}
                                            icon={<></>}
                                            className="absolute !bg-[#14232D] !flex-row-reverse !pr-0 mx-2 mt-2"
                                        />
                                    )}

                                    <ColorFinderFilter
                                        onFilterChange={handleColorFilterChange}
                                        getMediaControls={getMediaControls}
                                        tiler={tiler}
                                        cumulativeRgb={cumulativeRgb}
                                        activeSamples={activeSamples}
                                        onRemoveColorSample={onRemoveSample}
                                    />

                                    <div className="flex flex-col w-full px-4 pt-2 pb-5 gap-1">
                                        <div className="flex justify-between items-center h-8">
                                            <h5 className="text-white">Anomaly Filter</h5>
                                            <ToggleSwitch
                                                isToggled={anomalyEnabled}
                                                setIsToggled={(v) =>
                                                    dispatch(setColorFinderAnomalyEnabled(v))
                                                }
                                            />
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <div className="flex flex-col w-full">
                                                <div className="flex justify-between items-center">
                                                    <small>Opacity</small>
                                                    <small>{anomalyOpacity.toFixed(0)}%</small>
                                                </div>
                                                <Slider
                                                    onChange={(value) =>
                                                        dispatch(
                                                            setColorFinderAnomalyOpacity(value)
                                                        )
                                                    }
                                                    value={anomalyOpacity}
                                                />
                                            </div>
                                            <div className="flex flex-col w-full">
                                                <div className="flex justify-between items-center">
                                                    <small>Sensitivity</small>
                                                    <small>{anomalySensitivity.toFixed(0)}%</small>
                                                </div>
                                                <Slider
                                                    step={0.01}
                                                    onChange={(value) =>
                                                        dispatch(
                                                            setColorFinderAnomalySensitivity(value)
                                                        )
                                                    }
                                                    value={anomalySensitivity}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </ScrollableArea>
                    </Resizable>

                    <div
                        ref={mapRef}
                        className={classNames(
                            "w-full p-3 pt-2 min-h-[68px] basis-[68px] grow shrink",
                            {
                                hidden: isColorSampleTool,
                            }
                        )}
                    />
                </div>
            </div>
        </>
    )
}

const PointForm = () => {
    const dispatch = useAppDispatch()

    const iconTemplate = useAppSelector(selectIconTemplate)

    const nextIconNumber = useAppSelector(selectNextIconNumber)

    const defaultIconName = `Point ${nextIconNumber}`
    const numberStartAt = useAppSelector(selectIconNumberStartAt())

    const inputRef = useRef<HTMLInputElement>(null)

    useEffectUnsafe(() => {
        if (!inputRef.current) return
        if (iconTemplate.url.startsWith("number")) {
            inputRef.current.value = numberStartAt.toString()
        } else {
            inputRef.current.value = defaultIconName
        }
    }, [iconTemplate.url])

    useEffect(() => {
        dispatch(updateIconTemplate({ name: defaultIconName }))
    }, [defaultIconName, dispatch])

    return (
        <div className="flex flex-col gap-2.5 pt-2 px-3 pb-4">
            <div
                className="flex flex-col gap-2"
                onKeyDown={(e) => {
                    e.stopPropagation()
                }}
            >
                <small className="text-gray-100">Layer Name</small>
                <FormTextInput
                    ref={inputRef}
                    defaultValue={defaultIconName}
                    onChange={(e) => dispatch(updateIconTemplate({ name: e.target.value }))}
                    className="bg-gray-700 rounded-lg"
                />
            </div>

            <div className="flex flex-col">
                <IconPackDropdown
                    onChange={(iconPack) => {
                        if (iconPack === IconPackOption.Shape) {
                            dispatch(
                                updateIconTemplate({
                                    pack: IconPackOption.Shape,
                                    url: IconShape.Circle,
                                    color: encodeColorToNumber(defaultIconColor),
                                })
                            )
                        } else if (iconPack === IconPackOption.Number) {
                            dispatch(
                                updateIconTemplate({
                                    pack: iconPack,
                                    url: `numbers/${numberStartAt}`,
                                    color: numberIconColorSwatches[0],
                                    name: numberStartAt.toString(),
                                })
                            )
                        } else {
                            const pack = iconsByIconPack[iconPack]
                            const firstCategoryInPack = Object.values(pack)[0]
                            const firstIconInCategory = Object.values(firstCategoryInPack)[0]
                            dispatch(
                                updateIconTemplate({
                                    url: firstIconInCategory.url,
                                    pack: iconPack,
                                    color: iconTemplate.color,
                                })
                            )
                        }
                    }}
                    value={iconTemplate.pack}
                />

                {iconTemplate.pack === IconPackOption.Shape ? (
                    <>
                        <IconShapeDropdown
                            onChange={(value) => dispatch(updateIconTemplate({ url: value }))}
                            value={iconTemplate.url as IconShape}
                            color={iconTemplate.color}
                        />
                        <IconColorDropdown
                            onChange={(value) => dispatch(updateIconTemplate({ color: value }))}
                            value={iconTemplate.color}
                        />
                    </>
                ) : iconTemplate.pack === IconPackOption.Number ? (
                    <InspectorPanelIconNumberMenu
                        {...iconTemplate}
                        setColor={(color: number) => dispatch(updateIconTemplate({ color }))}
                        setOutline={(outline: number) => dispatch(updateIconTemplate({ outline }))}
                        setWidth={(width: number) => dispatch(updateIconTemplate({ width }))}
                    />
                ) : (
                    <IconDropdown
                        iconPack={iconTemplate.pack}
                        onChange={(value) => dispatch(updateIconTemplate({ url: value }))}
                        value={iconTemplate.url}
                    />
                )}
            </div>
        </div>
    )
}
